// Phase I Wave 2.4 · Impersonation Banner · persistent top-bar during
// super_admin impersonation mode. Reads the stored impersonation JWT,
// decodes exp, counts down, auto-logs-out when the TTL expires.
//
// Storage keys (localStorage):
//   impersonation_token · signed JWT (type=impersonation)
//   impersonation_meta  · {target_email, admin_email, exp} JSON
//
// The banner also shifts the top of the page down by its own height via
// an inline <style> rule on the <body> so content isn't hidden behind it.
import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const STORAGE_TOKEN_KEY = "impersonation_token";
const STORAGE_META_KEY = "impersonation_meta";
const BANNER_HEIGHT_PX = 44;


function _readMeta() {
    try {
        const token = localStorage.getItem(STORAGE_TOKEN_KEY);
        if (!token) return null;
        const metaRaw = localStorage.getItem(STORAGE_META_KEY);
        if (metaRaw) {
            const meta = JSON.parse(metaRaw);
            if (meta && typeof meta.exp === "number") return { token, ...meta };
        }
        // Fallback · decode exp from JWT payload (base64url).
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const pad = (s) => s + "=".repeat((4 - (s.length % 4)) % 4);
        const payloadStr = atob(pad(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
        const payload = JSON.parse(payloadStr);
        return {
            token,
            exp: payload.exp,
            target_email: payload.email || "",
            admin_email: (payload.impersonated_by || {}).admin_email || "",
        };
    } catch {
        return null;
    }
}


function _formatRemaining(seconds) {
    if (seconds <= 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}


export default function ImpersonationBanner() {
    const [meta, setMeta] = useState(() => _readMeta());
    const [remaining, setRemaining] = useState(() => {
        const m = _readMeta();
        if (!m) return 0;
        return Math.max(0, Math.floor(m.exp - Date.now() / 1000));
    });
    const tickRef = useRef(null);

    // Re-read when the window regains focus (admin may have logged in
    // via a tab-switch flow).
    useEffect(() => {
        const refresh = () => {
            const m = _readMeta();
            setMeta(m);
            setRemaining(m ? Math.max(0, Math.floor(m.exp - Date.now() / 1000)) : 0);
        };
        window.addEventListener("focus", refresh);
        window.addEventListener("storage", refresh);
        return () => {
            window.removeEventListener("focus", refresh);
            window.removeEventListener("storage", refresh);
        };
    }, []);

    // 1-second countdown · auto-logout on reaching zero.
    useEffect(() => {
        if (!meta) {
            if (tickRef.current) clearInterval(tickRef.current);
            return undefined;
        }
        tickRef.current = setInterval(() => {
            const r = Math.max(0, Math.floor(meta.exp - Date.now() / 1000));
            setRemaining(r);
            if (r <= 0) {
                // TTL expired · hard logout + redirect.
                handleForceLogout();
            }
        }, 1000);
        return () => {
            if (tickRef.current) clearInterval(tickRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta]);

    // Keep <body> padded so sticky elements don't hide behind the banner.
    useEffect(() => {
        if (!meta) {
            document.body.style.paddingTop = "";
            return;
        }
        document.body.style.paddingTop = `${BANNER_HEIGHT_PX}px`;
        return () => {
            document.body.style.paddingTop = "";
        };
    }, [meta]);

    const handleForceLogout = () => {
        try {
            localStorage.removeItem(STORAGE_TOKEN_KEY);
            localStorage.removeItem(STORAGE_META_KEY);
            // Wave 2.4 R2 · also clear the Bearer-injection access token
            // so apiFetch stops auto-attaching it to subsequent requests.
            localStorage.removeItem("impersonation_access_token");
        } catch {
            // noop · private-mode storage may throw
        }
        setMeta(null);
        setRemaining(0);
        // Hard redirect so protected routes reset their auth state.
        if (typeof window !== "undefined") {
            window.location.href = "/login";
        }
    };

    const handleStopImpersonate = async () => {
        const token = meta?.token;
        if (token) {
            try {
                await fetch("/api/admin/dev-tools/impersonate/stop", {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Impersonation-Token": token,
                    },
                });
            } catch {
                // Best-effort · still clear state locally.
            }
        }
        handleForceLogout();
    };

    if (!meta) return null;

    return (
        <div
            data-testid="impersonation-banner"
            style={{
                position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999,
                height: `${BANNER_HEIGHT_PX}px`, background: "#EF4444",
                color: "#FFFFFF", display: "flex", alignItems: "center",
                justifyContent: "space-between", padding: "0 16px",
                fontFamily: "-apple-system, 'SF Pro Text', system-ui, sans-serif",
                fontSize: 14, fontWeight: 500,
                boxShadow: "0 1px 0 rgba(0,0,0,0.1)",
            }}
            role="alert"
        >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <AlertTriangle size={16} aria-hidden="true" />
                <span>
                    Impersonate mode as{" "}
                    <strong data-testid="impersonation-banner-target">
                        {meta.target_email}
                    </strong>
                    {" · "}expires in{" "}
                    <span
                        data-testid="impersonation-banner-countdown"
                        style={{ fontFamily: "ui-monospace, Menlo, monospace" }}
                    >
                        {_formatRemaining(remaining)}
                    </span>
                </span>
            </div>
            <button
                type="button"
                onClick={handleStopImpersonate}
                data-testid="impersonation-banner-exit-btn"
                style={{
                    background: "#FFFFFF", color: "#EF4444",
                    border: "none", padding: "6px 12px", borderRadius: 6,
                    fontSize: 13, fontWeight: 500, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                }}
            >
                <X size={14} /> Exit impersonate
            </button>
        </div>
    );
}
